import type {
  ApolloCache,
  DefaultContext,
  MutationFunctionOptions,
  OperationVariables,
} from '@apollo/client';
import {
  addDays,
  addMilliseconds,
  addMinutes,
  differenceInMilliseconds,
  differenceInMinutes,
  endOfDay,
  format,
  isAfter,
  isBefore,
} from 'date-fns';
import { Frequency, RRuleSet } from 'rrule';
import type { EventData, NoteContent } from '../../../hooks/types';
import { eventData } from '../../../lib/graphql/common/queries';
import { compact } from '../../../lib/utils';
import { customMillisecondsToHours } from '../../../lib/utils/date-time';
import type { FilledShift, ShiftCellRecord, SimpleUser } from '../types';
import {
  maximumShiftPeriodInHours,
  millisecondsPerMinute,
  minimumShiftPeriodInHours,
} from './constants';
import {
  localDateFromRRuleDate,
  newRecurRule,
  newRecurRuleSet,
  parseRRuleSet,
} from './RRule';
import {
  addChangelogs,
  jsonifyNotesNoChangelog,
  shiftDeletedNote,
  shiftExceptionCreatedNote,
} from './changeLog';
import { toNoteContent } from '../../Notes/util';

type ModifyShiftFunc = (
  options?:
    | MutationFunctionOptions<
        {
          readonly insert_event_recurring_shift_exception_created?:
            | {
                readonly affected_rows: number;
              }
            | undefined;
          readonly insert_event_shift_created?:
            | {
                readonly affected_rows: number;
              }
            | undefined;
          readonly insert_event_shift_updated?:
            | {
                readonly affected_rows: number;
              }
            | undefined;
          readonly insert_event_shift_deleted?:
            | {
                readonly affected_rows: number;
              }
            | undefined;
        },
        OperationVariables,
        DefaultContext,
        ApolloCache<unknown>
      >
    | undefined
) => Promise<unknown>;

type SaveShiftParams = {
  readonly currentShift: FilledShift | undefined;
  readonly isNewShift: boolean;
  readonly selectedUsers: readonly SimpleUser[];
  readonly shiftProps: ShiftProps;
  readonly shiftUserRoleAssignmentId?: string;
};

type RemoveSingleRecurringShiftParams = {
  readonly recurringProps: ShiftRecurringProps;
  readonly shiftProps: ShiftProps;
  readonly isDerived: boolean;
  readonly shiftUserRoleAssignmentId?: string;
  readonly shiftRecurrenceRule?: RRuleSet;
};

type RecurrentShiftProps = {
  readonly currentShift: FilledShift;
  readonly initialRecurShiftStartDate: Date;
  readonly startTime: Date | undefined;
  readonly endTime: Date | undefined;
  readonly shiftStatus: string;
  readonly shiftNote: string | undefined;
  readonly isRecurring: boolean;
  readonly shiftRecurrenceRule: RRuleSet | undefined;
  readonly shiftRecurEndDate: Date | null;
  readonly record: ShiftCellRecord;
  readonly selectedUsers: readonly SimpleUser[];
};

type ShiftProps = {
  readonly id?: string;
  readonly startTime?: Date;
  readonly endTime?: Date;
  readonly status: string;
  readonly note?: string;
  readonly isRecurring: boolean;
  readonly recurrencePattern?: string;
  readonly recurrenceEndDate: Date | null;
  readonly teamRoleAssignmentId: string;
  readonly rootId: string | undefined;
};

type ShiftRecurringProps = {
  readonly freq: Frequency;
  readonly interval: number;
  readonly end?: Date | null;
  readonly shiftStartTime: string;
  readonly shiftEndTime: string;
};

// The base fields required to submit a create or update mutation
export type CreateOrUpdateShift = {
  readonly start_time?: Date;
  readonly end_time?: Date;
  readonly shift_status: string;
  readonly note: { readonly content: string } | undefined;
  readonly is_recurring: boolean;
  readonly recurrence_pattern?: string;
  readonly recurrence_end_date?: Date | null;
  readonly user_id?: string;
  readonly team_role_assignment_id?: string;
  readonly user_role_assignment_id?: string;
  readonly event: EventData;
};

export type ShiftToCreate = CreateOrUpdateShift & {
  readonly root_id: string | undefined;
};

export type ShiftToUpdate = CreateOrUpdateShift & {
  readonly shift_id: string | undefined;
};

export type ShiftToDelete = {
  readonly shift_id: string | undefined;
  readonly event: EventData;
};

export type ShiftExceptionToCreate = {
  readonly shift_id: string | undefined;
  readonly exception_date: Date | string | undefined;
  readonly event: EventData;
};

export type ShiftExceptionDelete = {
  readonly recurring_shift_id: string | undefined;
  readonly exception_date: Date | string | undefined;
  readonly event: EventData;
};

export type ShiftExceptionUpdate = ShiftExceptionDelete &
  Omit<
    CreateOrUpdateShift,
    'is_recurring' | 'recurrence_pattern' | 'recurrence_end_date'
  >;

export type NoteToCreate = {
  readonly content: NoteContent;
  readonly object_id: string;
  readonly event: EventData;
};

export type ShiftModifiers = {
  readonly shiftsToCreate: readonly ShiftToCreate[];
  readonly shiftsToUpdate: readonly ShiftToUpdate[];
  readonly shiftsToDelete: readonly ShiftToDelete[];
  readonly shiftExceptionUpdates: readonly ShiftExceptionUpdate[];
  readonly shiftExceptionDeletes: readonly ShiftExceptionDelete[];
  /**
   * We only use notesToCreate to create notes for EXCEPTIONS and DELETES;
   * CREATES and UPDATES have their notes created within the database triggers.
   */
  readonly notesToCreate: readonly NoteToCreate[];
};

export const emptyShiftModifiers = {
  shiftsToCreate: [],
  shiftsToUpdate: [],
  shiftsToDelete: [],
  shiftExceptionUpdates: [],
  shiftExceptionDeletes: [],
  notesToCreate: [],
};

/**
 * Creates a note to create.
 * @param priorityShiftId - the shift id to prioritize. Priority: rootId > originalShiftId > shiftId
 * @param content - the content of the note
 * @returns a note to create
 */
export const createNoteToCreate = (
  priorityShiftId: string,
  content: NoteContent
): NoteToCreate => ({
  content,
  object_id: priorityShiftId,
  event: eventData('note_created'),
});

const shiftMessage = <T>(shifts: readonly T[], action: string) =>
  shifts.length === 0
    ? undefined
    : `Shift${shifts.length > 1 ? 's' : ''} ${action}`;

export const performModifyShift = async (
  modifyShift: ModifyShiftFunc,
  modifiers: ShiftModifiers,
  onCompleted: (message: string) => void
): Promise<void> => {
  const messages: readonly string[] = compact(
    Array.from(
      new Set(
        [
          shiftMessage(modifiers.shiftsToDelete, 'Deleted'),
          shiftMessage(modifiers.shiftsToCreate, 'Created'),
          shiftMessage(modifiers.shiftsToUpdate, 'Updated'),
          shiftMessage(modifiers.shiftExceptionUpdates, 'Updated (Recurring)'),
          shiftMessage(modifiers.shiftExceptionDeletes, 'Deleted (Recurring)'),
          modifiers.notesToCreate.map((n) => n.content.content),
        ].flat()
      )
    )
  );

  await modifyShift({
    variables: modifiers,
    onCompleted: () => onCompleted(messages.join(' + ')),
    refetchQueries: 'active',
  });
};

/**
 * Validates shift data and returns the specific error message.
 * @param shiftDurationInHoursIsValid if the duration of the shift is valid.
 * @param startTime start time of the shift.
 * @param endTime end time of the shift.
 * @returns errors gathered for the shift validation, empty if no error found.
 */
export const shiftErrors = (
  shiftDurationInHoursIsValid: boolean,
  startTime?: Date,
  endTime?: Date
) => {
  const errors: readonly string[] = [
    startTime === undefined ? 'Start time is missing' : undefined,
    endTime === undefined ? 'End time is missing' : undefined,
    !shiftDurationInHoursIsValid ? 'Shift duration is invalid' : undefined,
    startTime !== undefined &&
    endTime !== undefined &&
    isAfter(startTime, endTime)
      ? 'Start time is after end time'
      : undefined,
  ].filter((error): error is string => error !== undefined);

  return errors;
};

/**
 * Creates a vacant shift, that is without user role assignment, but it has team role assignment to determine the
 * shift's role and team.
 * @param shiftProps shift properties.
 * @param modifyShift function to create the shift.
 * @returns promise of create shift result as unknown since we don't use the result.
 */
const createVacantShift = (shiftProps: ShiftProps): ShiftModifiers => {
  const vacantShift: ShiftToCreate = {
    root_id: shiftProps.rootId ?? shiftProps.id,
    start_time: shiftProps.startTime,
    end_time: shiftProps.endTime,
    shift_status: shiftProps.status,
    note: toNoteContent(shiftProps.note),
    is_recurring: shiftProps.isRecurring,
    recurrence_pattern: shiftProps.recurrencePattern,
    recurrence_end_date: shiftProps.recurrenceEndDate,
    team_role_assignment_id: shiftProps.teamRoleAssignmentId,
    event: eventData('shift_created'),
  };

  return {
    ...emptyShiftModifiers,
    shiftsToCreate: [vacantShift],
  };
};

/**
 * Creates a shift with personnel filled.
 * Multiple shifts will be created for each selected user.
 * @param selectedUserIds user ids for the users that will be assigned to the shift.
 * @param shiftProps shift properties.
 * @param modifyShift function to create the shift.
 * @returns promise of create shift result as unknown since we don't use the result.
 */
const createFilledShift = (
  selectedUserIds: readonly string[],
  shiftProps: ShiftProps
): ShiftModifiers => {
  // Multiple shifts will be created for all selected users.
  const shiftsToCreate: readonly ShiftToCreate[] = selectedUserIds.map(
    (userId): ShiftToCreate => ({
      // Multiple users can only be provided when the shift is being made for the first time,
      // so there will not be a root_id.
      // If this is changed in the future, such that multiple users can be assigned to
      // an already established shift, we may need to provide a root_id here.
      root_id: undefined,
      start_time: shiftProps.startTime,
      end_time: shiftProps.endTime,
      shift_status: shiftProps.status,
      note: toNoteContent(shiftProps.note),
      is_recurring: shiftProps.isRecurring,
      recurrence_pattern: shiftProps.recurrencePattern,
      recurrence_end_date: shiftProps.recurrenceEndDate,
      user_id: userId,
      team_role_assignment_id: shiftProps.teamRoleAssignmentId,
      event: eventData('shift_created'),
    })
  );

  return {
    ...emptyShiftModifiers,
    shiftsToCreate,
  };
};

/**
 * Updates a vacant shift. A user can be assigned to the provided shift, if a user is assigned, the vacant shift will turn
 * into a filled shift with user and no longer be vacant.
 * @param shiftProps shift properties.
 * @param modifyShift update shift function.
 * @param newOrUpdatedUser optional user id for the user to be assigned to the shift.
 * @returns promise of update shift result as unknown since we don't use the result.
 */
const updateVacantShift = (
  shiftProps: ShiftProps,
  newOrUpdatedUser?: SimpleUser
): ShiftModifiers => {
  const shiftToUpdate: ShiftToUpdate = {
    user_id: newOrUpdatedUser?.userId,
    shift_id: shiftProps.id,
    start_time: shiftProps.startTime,
    end_time: shiftProps.endTime,
    shift_status: shiftProps.status,
    note: toNoteContent(shiftProps.note),
    is_recurring: shiftProps.isRecurring,
    team_role_assignment_id: shiftProps.teamRoleAssignmentId,
    recurrence_pattern: shiftProps.recurrencePattern,
    recurrence_end_date: shiftProps.recurrenceEndDate,
    event: eventData('shift_updated'),
  };

  return {
    ...emptyShiftModifiers,
    shiftsToUpdate: [shiftToUpdate],
  };
};

/**
 * Updates a shift with user filled (non-vacant).
 * If a user id is provided, it means the shift needs to change the assigned user. A new shift with the provided user assigned
 * will be created and the previous shift with old user will be deleted.
 * @param shiftProps shift properties.
 * @param modifyShift modify shift function.
 * @param userId optional user id for the new user to be assigned to the shift.
 * @returns update shift result as unknown since we don't use the result.
 */
const updateFilledShift = (
  shiftProps: ShiftProps,
  newOrUpdatedUser?: SimpleUser,
  currentUserId?: string
): ShiftModifiers => {
  if (
    newOrUpdatedUser?.userId !== undefined &&
    currentUserId !== newOrUpdatedUser.userId
  ) {
    const shiftWithNewUser: ShiftToCreate = {
      root_id: shiftProps.rootId ?? shiftProps.id,
      start_time: shiftProps.startTime,
      end_time: shiftProps.endTime,
      shift_status: shiftProps.status,
      note: toNoteContent(shiftProps.note),
      is_recurring: shiftProps.isRecurring,
      recurrence_pattern: shiftProps.recurrencePattern,
      user_id: newOrUpdatedUser.userId,
      team_role_assignment_id: shiftProps.teamRoleAssignmentId,
      event: eventData('shift_created'),
    };

    const shiftsToDelete: readonly ShiftToDelete[] =
      shiftProps.id === undefined
        ? []
        : [
            {
              shift_id: shiftProps.id,
              event: eventData('shift_deleted'),
            },
          ];

    // Write a note for the deleted shift
    const notesToCreate: readonly NoteToCreate[] =
      shiftProps.id === undefined || shiftProps.startTime === undefined
        ? []
        : [
            createNoteToCreate(
              shiftProps.rootId ?? shiftProps.id,
              shiftDeletedNote(shiftProps.startTime)
            ),
          ];

    return {
      ...emptyShiftModifiers,
      shiftsToCreate: [shiftWithNewUser],
      shiftsToDelete,
      notesToCreate,
    };
  }

  const shiftToUpdate: ShiftToUpdate = {
    shift_id: shiftProps.id,
    start_time: shiftProps.startTime,
    end_time: shiftProps.endTime,
    shift_status: shiftProps.status,
    note: toNoteContent(shiftProps.note),
    is_recurring: shiftProps.isRecurring,
    recurrence_pattern: shiftProps.recurrencePattern,
    recurrence_end_date: shiftProps.recurrenceEndDate,
    event: eventData('shift_updated'),
  };

  return {
    ...emptyShiftModifiers,
    shiftsToUpdate: [shiftToUpdate],
  };
};

/**
 * Update the recurrent shift end date.
 * Get the updated shift params for modify shift query.
 */
export const shiftEndDateUpdateOptions = (
  currentShift: FilledShift,
  newRecurEndDate: Date,
  notes: string | undefined
): ShiftToUpdate | undefined => {
  // Exit if current shift is not a recurrent shift
  if (
    currentShift.recurrencePattern === undefined ||
    currentShift.originalShiftId === undefined
  ) {
    return undefined;
  }

  const initialShiftRecurRuleSet = parseRRuleSet(
    currentShift.recurrencePattern
  );

  const firstRecurRule = initialShiftRecurRuleSet.rrules()[0];

  // Exit if no RRule is found.
  if (firstRecurRule === undefined) {
    return undefined;
  }

  const originalShiftStartTime = localDateFromRRuleDate(
    firstRecurRule.options.dtstart
  );

  const shiftDuration = differenceInMillisecondsWithoutTimezone(
    new Date(currentShift.endTime),
    new Date(currentShift.startTime)
  );

  const originalShiftEndTime = addMilliseconds(
    originalShiftStartTime,
    shiftDuration
  );

  const nextShiftRecurRuleSet = new RRuleSet();

  initialShiftRecurRuleSet.rrules().map((rrule) =>
    nextShiftRecurRuleSet.rrule(
      newRecurRule({
        ...rrule.options,
        dtstart: localDateFromRRuleDate(rrule.options.dtstart),
        until: newRecurEndDate,
      })
    )
  );

  return {
    shift_id: currentShift.originalShiftId,
    start_time: originalShiftStartTime,
    end_time: originalShiftEndTime,
    shift_status: currentShift.status,
    note: toNoteContent(notes),
    is_recurring: currentShift.isRecurring,
    recurrence_pattern: nextShiftRecurRuleSet.toString(),
    recurrence_end_date: newRecurEndDate,
    event: eventData('shift_updated'),
  };
};

/**
 * Removes a root recurring shift only.
 * We need to make the next available recurring shift as root shift in this case.
 * @param shiftRecurringProps recurring properties of the shift.
 * @param shiftProps shift properties.
 * @param shiftRecurrenceRule the shift's recurring rule.
 * @param modifyShift modify shift function.
 * @param shiftUserRoleAssignmentId user role assignment of this shift, if it is assigned to a user.
 * @returns modify shift result as unknown since we don't use the result.
 */
const removeRootRecurringShiftModifiers = (
  shiftRecurringProps: ShiftRecurringProps,
  shiftProps: ShiftProps,
  shiftRecurrenceRule: RRuleSet,
  shiftUserRoleAssignmentId?: string
): ShiftModifiers => {
  const shiftsToDelete: readonly ShiftToDelete[] =
    shiftProps.id === undefined
      ? []
      : [
          {
            shift_id: shiftProps.id,
            event: eventData('shift_deleted'),
          },
        ];

  const notesToCreate: readonly NoteToCreate[] =
    shiftProps.id === undefined || shiftProps.startTime === undefined
      ? []
      : [
          createNoteToCreate(
            shiftProps.rootId ?? shiftProps.id,
            shiftDeletedNote(shiftProps.startTime)
          ),
        ];

  // The RRule has incorrect type for the after, it should return Date or null as type.
  const nextShiftStartTime = shiftRecurrenceRule.after(
    new Date(shiftRecurringProps.shiftStartTime)
  );

  if (nextShiftStartTime === null) {
    return {
      ...emptyShiftModifiers,
      shiftsToDelete,
      notesToCreate,
    };
  }

  const shiftDurationMillis =
    new Date(shiftRecurringProps.shiftEndTime).getTime() -
    new Date(shiftRecurringProps.shiftStartTime).getTime();

  const nextRecurrenceRule = newRecurRuleSet({
    freq: shiftRecurringProps.freq,
    interval: shiftRecurringProps.interval,
    dtstart: nextShiftStartTime,
    until: shiftRecurringProps.end,
    byweekday: shiftRecurrenceRule.options.byweekday,
  });

  const shiftToCreate: ShiftToCreate = {
    root_id: shiftProps.rootId ?? shiftProps.id,
    start_time: nextShiftStartTime,
    end_time: addMilliseconds(nextShiftStartTime, shiftDurationMillis),
    shift_status: shiftProps.status,
    note: toNoteContent(shiftProps.note),
    is_recurring: true,
    recurrence_pattern: nextRecurrenceRule.toString(),
    user_role_assignment_id: shiftUserRoleAssignmentId,
    team_role_assignment_id: shiftProps.teamRoleAssignmentId,
    event: eventData('shift_created'),
  };

  return {
    ...emptyShiftModifiers,
    shiftsToCreate: [shiftToCreate],
    shiftsToDelete,
    notesToCreate,
  };
};

/**
 * Saves the provided shift. Either creates or updates the shift.
 * Based on the provided user role assignment, creates or updates filled or vacant shifts.
 * @param params save shift parameters.
 * @returns promise of the save shift result as unknown as we don't use the result.
 */
export const saveSingleShiftModifiers = ({
  currentShift,
  shiftProps,
  isNewShift,
  selectedUsers,
  shiftUserRoleAssignmentId,
}: SaveShiftParams): ShiftModifiers => {
  if (!isNewShift && currentShift !== undefined) {
    // Existing shift(s) ∴ 1 user
    const selectedUser = selectedUsers.at(0);

    const shiftModifiers =
      shiftUserRoleAssignmentId === undefined
        ? // Vacant
          updateVacantShift(shiftProps, selectedUser)
        : // Filled
          updateFilledShift(
            shiftProps,
            selectedUser,
            currentShift.user?.userId
          );

    return addChangelogs(currentShift, selectedUser, shiftModifiers);
  }

  // New shift(s) ∴ maybe > 1 user
  const shiftModifiers =
    selectedUsers.length === 0
      ? // Vacant
        createVacantShift(shiftProps)
      : // Filled
        createFilledShift(
          selectedUsers.map(({ userId }) => userId),
          shiftProps
        );

  return shiftProps.startTime === undefined
    ? emptyShiftModifiers
    : jsonifyNotesNoChangelog(
        format(new Date(shiftProps.startTime), 'yyyy-MM-dd'),
        shiftModifiers
      );
};

/**
 * Deletes a single recurring shift.
 * If the shift is derived, shift exception will be created.
 * @param params remove single recurring shift params.
 * @returns remove shift result as unknown since we don't use the result.
 */
export const removeSingleRecurringShiftModifiers = ({
  isDerived,
  recurringProps,
  shiftRecurrenceRule,
  shiftProps,
  shiftUserRoleAssignmentId,
}: RemoveSingleRecurringShiftParams): ShiftModifiers => {
  if (!isDerived && shiftRecurrenceRule !== undefined) {
    return removeRootRecurringShiftModifiers(
      recurringProps,
      shiftProps,
      shiftRecurrenceRule,
      shiftUserRoleAssignmentId
    );
  }

  const makeException =
    isDerived &&
    shiftProps.startTime !== undefined &&
    shiftProps.id !== undefined;

  const shiftExceptionDeletes: readonly ShiftExceptionDelete[] = makeException
    ? [
        {
          recurring_shift_id: shiftProps.id,
          exception_date: shiftProps.startTime,
          event: eventData('derived_shift_deleted'),
        },
      ]
    : [];

  const notesToCreate: readonly NoteToCreate[] = makeException
    ? [
        createNoteToCreate(
          shiftProps.rootId ?? shiftProps.id,
          shiftExceptionCreatedNote(new Date(shiftProps.startTime))
        ),
      ]
    : [];

  return {
    ...emptyShiftModifiers,
    shiftExceptionDeletes,
    notesToCreate,
  };
};

/**
 * Gets id of the root shift
 * Priority: rootId > originalShiftId > shiftId
 *
 * When a shift is created from scratch, it doesn't have a root id because its own id is the root id. It's only after a shift is updated from one user to another that the shift has a root_id that differs from the original.
 *
 * Additionally, when the backend generates derived shifts, it gives them the id derived-${index}-${shift.id}-${startTime.valueOf()}-${updatedEndTime.valueOf()},
 * and it puts the id of the recurring shift into `originalShiftId.
 *
 * To get the root id, we see if the shift has a rootId, and then fallback to the originalShiftId, finally falling back to the shiftId.
 * @param shift - the shift to get the priority shift id for
 * @returns the priority shift id for the shift
 */
export const rootId = (shift: FilledShift): string =>
  shift.rootId ?? shift.originalShiftId ?? shift.id;

/**
 * Creates the shift modifiers for a future recurrent shift
 */
export const futureRecurrentShiftModifiers = ({
  currentShift,
  initialRecurShiftStartDate,
  startTime,
  endTime,
  shiftStatus,
  shiftNote: shiftNotes,
  isRecurring,
  shiftRecurrenceRule,
  shiftRecurEndDate,
  record,
  selectedUsers,
}: RecurrentShiftProps): ShiftModifiers => {
  const newEndDateForOriginalShift = endOfDay(
    addDays(new Date(currentShift.startTime), -1)
  );

  const originalShiftStartTime = localDateFromRRuleDate(
    initialRecurShiftStartDate
  );

  const shiftToCreate: ShiftToCreate = {
    root_id: rootId(currentShift),
    start_time: startTime,
    end_time: endTime,
    shift_status: shiftStatus,
    note: toNoteContent(shiftNotes),
    is_recurring: isRecurring,
    recurrence_pattern: shiftRecurrenceRule?.toString(),
    recurrence_end_date: shiftRecurEndDate,
    team_role_assignment_id: record.teamRoleAssignmentId,
    user_id:
      (selectedUsers.length === 1 ? selectedUsers[0] : undefined)?.userId ??
      record.userId,
    event: eventData('shift_created'),
  };

  // Delete the recurrent shift directly if recurrent end date is before start time.
  const shiftModifiers: ShiftModifiers =
    isBefore(newEndDateForOriginalShift, originalShiftStartTime) &&
    currentShift.originalShiftId !== undefined
      ? {
          ...emptyShiftModifiers,
          shiftsToCreate: [shiftToCreate],
          shiftsToDelete: [
            {
              shift_id: currentShift.originalShiftId,
              event: eventData('shift_deleted'),
            },
          ],
          notesToCreate: [
            createNoteToCreate(
              rootId(currentShift),
              shiftDeletedNote(new Date(currentShift.startTime))
            ),
          ],
        }
      : {
          ...emptyShiftModifiers,
          shiftsToCreate: [shiftToCreate],
          shiftsToUpdate: compact([
            shiftEndDateUpdateOptions(
              currentShift,
              newEndDateForOriginalShift,
              shiftNotes
            ),
          ]),
        };

  return shiftModifiers;
};

/**
 * Creates the shift modifiers to remove a future recurrent shift
 */
export const removeFutureRecurrentShiftModifiers = (
  currentShift: FilledShift,
  initialRecurShiftStartDate: Date,
  shiftNotes: string | undefined
): ShiftModifiers => {
  const newRecurEndDate = endOfDay(
    addDays(new Date(currentShift.startTime), -1)
  );

  const originalShiftStartTime = localDateFromRRuleDate(
    initialRecurShiftStartDate
  );

  // Delete the recurrent shift directly if recurrent end date is before start time.
  const shiftModifiers: ShiftModifiers =
    isBefore(newRecurEndDate, originalShiftStartTime) &&
    currentShift.originalShiftId !== undefined
      ? {
          ...emptyShiftModifiers,
          shiftsToDelete: [
            {
              shift_id: currentShift.originalShiftId,
              event: eventData('shift_deleted'),
            },
          ],
          notesToCreate: [
            createNoteToCreate(
              rootId(currentShift),
              shiftDeletedNote(new Date(currentShift.startTime))
            ),
          ],
        }
      : {
          ...emptyShiftModifiers,
          shiftsToUpdate: compact([
            shiftEndDateUpdateOptions(
              currentShift,
              newRecurEndDate,
              shiftNotes
            ),
          ]),
        };

  return shiftModifiers;
};

/**
 * Validates the shift's duration.
 * @param shiftDurationInHours shift duration in hours.
 * @returns true if within the allowed shift duration hours, otherwise false.
 */
export const validateShiftDuration = (shiftDurationInHours: number) =>
  shiftDurationInHours >= minimumShiftPeriodInHours &&
  shiftDurationInHours <= maximumShiftPeriodInHours;

/**
 * Calculate the absolute difference in minutes between two date without timezone.
 * E.g. difference of (6:00am GMT+11) and (0:00am GMT+10) will be 6 hours.
 * Since we used the difference to calculate the absolute position of shifts,
 * shifts' position on daylight saving boundary will be wrong if we consider the timezone.
 * E.g. difference of (6:00am GMT+11) and (0:00am GMT+10) will be 7 hours if we
 * consider the timezone change on daylight saving boundary, but we still want to show the
 * shifts which start at (6:00am GMT+11) at 6/24 position.
 * @param dateLeft later date.
 * @param dateRight earlier date.
 * @returns difference in minutes between two input date.
 */
export const differenceInMinutesWithoutTimezone = (
  dateLeft: Date,
  dateRight: Date
) =>
  differenceInMinutes(dateLeft, dateRight) -
  (dateLeft.getTimezoneOffset() - dateRight.getTimezoneOffset());

/**
 * Calculate the absolute difference in milliseconds between two date without timezone.
 * We calculate the shift background color based difference in milliseconds.
 * But on daylight saving boundary date, the background color will be shifted 1 hour earlier
 * or late due to the timezone change.
 * We use this function to calculate the difference in milliseconds without timezone change
 * to get the correct shift background color on boundary date.
 * @param dateLeft later date.
 * @param dateRight earlier date.
 * @returns difference in milliseconds between two input date.
 */
export const differenceInMillisecondsWithoutTimezone = (
  dateLeft: Date,
  dateRight: Date
) =>
  differenceInMilliseconds(dateLeft, dateRight) -
  (dateLeft.getTimezoneOffset() - dateRight.getTimezoneOffset()) *
    millisecondsPerMinute;

/**
 * Consider the timezone changed caused by daylight saving for shift start time.
 * E.g. the daylight saving change happens at 2:00/3:00 am,
 * so when we use the startOfDay() on those boundary dates,
 * and the shift start time is after the change, the shift start time will have
 * a different timezone from the date got by startOfDay(). (AEST and AEDT in our case)
 * @param startDate selected date (cell date) on roster.
 * @param offsetHours offset hour for the initial start/end time based on shift period.
 * @returns shift initial start time .
 */
export const startTimeWithDaylightSaving = (
  startDate: Date,
  offsetHours: number
) => {
  const originalStartTime = addMinutes(startDate, offsetHours * 60);

  return addMinutes(
    originalStartTime,
    originalStartTime.getTimezoneOffset() - startDate.getTimezoneOffset()
  );
};

/**
 * Initial shift time when loaded.
 * @param offsetHours offset hour for the initial start/end time based on shift period.
 * @param time used to set initial shift time.
 * @param record shift cell record with selected date (cell date) on roster.
 * @returns initial time or undefined.
 */
export const initialTime = (
  offsetHours: number,
  time?: Date,
  record?: ShiftCellRecord
): Date | undefined =>
  record === undefined
    ? undefined
    : time ?? startTimeWithDaylightSaving(record.selectedDate, offsetHours);

/**
 * Shift duration in hours.
 * @param defaultHours default shift duration in hours.
 * @param startTime shift start time.
 * @param endTime shift end time.
 * @returns either shift duration in hours, or the default hours.
 */
export const durationOrDefault = (
  defaultHours: number,
  startTime?: Date,
  endTime?: Date
): number =>
  startTime !== undefined && endTime !== undefined
    ? customMillisecondsToHours(endTime.getTime() - startTime.getTime())
    : defaultHours;
/**
 * Adjust the to create recurring shift end time if the shift cross the timezone change on daylight saving boundary date.
 * @param isRecurring if the shift is recurring.
 * @param startTime shift start time.
 * @param endTime shift end time,
 * @returns adjusted shift end time.
 */
export const adjustRecurEndTime = (
  isRecurring: boolean,
  startTime?: Date,
  endTime?: Date
) => {
  if (startTime === undefined || endTime === undefined) {
    return undefined;
  }

  if (
    !isRecurring ||
    startTime.getTimezoneOffset() === endTime.getTimezoneOffset()
  ) {
    return endTime;
  }

  return addMinutes(
    endTime,
    startTime.getTimezoneOffset() - endTime.getTimezoneOffset()
  );
};
